/* eslint-disable max-len */
import _ from 'underscore';
import Promise from 'bluebird';
import ReactDOM from 'react-dom';
import React from 'react';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { BackboneSchema as backboneSchema } from '@bingads-webui/backbone-schema';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import {
  CompanyNameCriterionSetting,
  JobFunctionCriterionSetting,
  IndustryCriterionSetting,
  AgeCriterionSetting,
  DeviceCriterionSetting,
  GenderCriterionSetting,
  AudienceCriterionSetting,
  LocationCriterionSetting,
  PlacementSetting,
} from '@bingads-webui-campaign/audience-criterion-setting';
import { InputControl } from '@bingads-webui/input-control';
import { RemainingCharsHint } from '@bingads-webui/remaining-chars-hint';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { ReachEstimateControl, EstimateRequestFactory } from '@bingads-webui-campaign/reach-estimate-control';
import { shouldSupportCashbackSettings } from '@bingads-webui-campaign/personalized-offer-shared';
import {
  AudienceTargetSelector,
  AudienceCampaignErrorsView,
  AudienceStepBase,
  criterionHelper,
  getBulkErrorsLogMessage,
  BidStrategy,
  getFeedInfo,
  generateNameSuggestionSuffix,
  isFeedBasedAudienceCampaign,
} from '@bingads-webui-campaign/audience-campaign-shared';
import { getLocationsInfo, showIMAReachModal, getLocationsInfoFromCampaignCriterions } from '@bingads-webui-campaign-react/ima-reach-representation';
import { getBlackListCriterions } from '@bingads-webui-campaign/audience-advanced-target-settings';
import { BoostPlacementTarget } from '@bingads-webui-campaign-react/boost-placement-target';
import { ContextualTargetWithStyle, getSelectionFromCriterions, getContextualTargetInfo } from '@bingads-webui-campaign-react/boost-contextual-target';

import template from './template.pug';
import collapseTemplate from './audience-target-selector-collapse.pug';
import { OptTgtToggle } from './OptTgt-toggle';

/**
 * CrudAudienceAdgroup class that extends {AudienceStepBase} class
 * The class defines the section of the setting AdGroup with the targets
 *
 * @class CrudAudienceAdgroup
 * @extends {AudienceStepBase}
 */
export class CrudAudienceAdgroup extends AudienceStepBase {
  /**
   * Initializes the {CrudAudienceAdgroup} class.
   *
   * @param {Object} options - options for initializing the class.
   * @param {Object} options.viewContext = viewContext for the View.
   * @returns {undefined}
   */
  initialize(options) {
    this.renderMarker;
    this.perfMarker = options.perfMarker;
    this.perfMarker.willInit();
    this.isAdImpressions = options.isAdImpressions;
    this.wizardElement = options.wizardElement;
    this.viewContext = options.viewContext || {};
    this.viewContext.level = 'adGroup';
    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    this.i18nErrors = DependencyResolver.get('component/api-errors-campaign/index');
    this.audienceCampaignType = options.audienceCampaignType || 'Audience';
    this.scenario = options.scenario || '';
    this.currentLevelTargetInfo = options.currentLevelTargetInfo;
    this.saveOnce = false;
    this.currentCampaign = this.viewContext.currentCampaign;
    this.options = options;

    const schema = DependencyResolver.get('$/schema!campaign/AdGroup');
    const SchemaModel = backboneSchema(
      schema,
      this.i18n,
      ['Name', 'Medium', 'PricingModel', 'Campaign']
    );
    this.isSimplifyCampaignCreation = this.scenario === 'simplify-campaign-creation';

    const config = DependencyResolver.get('component/config/index');
    this.accountInfo = config.get('CurrentAccount');
    this.permissions = config.get('Permissions');
    this.isIMAReachRepresentationPhase1Enabled = _.result(this.permissions, 'IsIMAReachRepresentationPhase1Enabled');
    this.isAudienceCampaignCollapseTargetSelectionEnable =
      _.result(this.permissions, 'IsAudienceCampaignCollapseTargetSelectionEnabled', false);

    this.isMSANRemoveDeviceTargetsEnabled = _.result(this.permissions, 'IsMSANRemoveDeviceTargetsEnabled');
    this.IsDomesticSimplifiedChineseEnabled = _.result(this.permissions, 'IsDomesticSimplifiedChineseEnabled');
    this.isShowDisclaimerBannerEnabled = _.get(this.permissions, ['dynamic', 'AgeGenderDisclaimer'], false);
    this.Enable1PPlacementTargeting = _.get(this.permissions, ['dynamic', 'Enable1PPlacementTargeting'], false);
    this.isBoostConversionBasedSegmentEnabled = _.get(this.permissions, ['dynamic', 'BoostConversionBasedSegment'], false) &&
      _.get(this.permissions, ['IsAmplifyEnabled'], false);

    this.boostPlacementReactComponent = React.createRef();
    this.optTgtReactComponent = React.createRef();

    this.isMSANBoostEnabled = _.result(this.permissions, 'IsAmplifyEnabled', false);
    this.isContextualTargetEnabled = this.isMSANBoostEnabled &&
      _.result(this.permissions, 'IsContextualTargetEnabled', false);

    this.workflow = options.workflow;

    this.model = new SchemaModel();
    this.logScenario = options.logScenario;
    this.activityLogger = this.logScenario.createAdGroup.create();
    this.applyActivity = options.applyActivity;
    this.isOpenCreatePanel = options.isOpenCreatePanel;
    this.isLastStep = options.lastStep === 'CrudAudienceAdgroup';
    this.isForVNext = options && options.isForVNext;
    // Location is selected by default, Device is hidden from the selector
    this.audienceTargetSelector = new AudienceTargetSelector({
      layout: this.isAudienceCampaignCollapseTargetSelectionEnable ? 'collapse' : 'card',
      selectedTypeKeys: [],
      blackListKeys: this.IsDomesticSimplifiedChineseEnabled ? getBlackListCriterions({
        campaignType: this.audienceCampaignType.toLowerCase(),
        permissions: this.permissions || {},
      }) : _.compact(['device', this.audienceCampaignType === 'shopping' && 'placement']),
      activityLogger: this.activityLogger,
      permissions: this.permissions,
    });

    // mock
    // this.currentCampaign.CampaignCashback = { MonthlyBudget: 1 };

    this.supportCashbackSettings = shouldSupportCashbackSettings(this.currentCampaign, this.permissions);

    const settingOptions = {
      viewContext: this.viewContext,
      currentLevelTargetInfo: this.currentLevelTargetInfo,
      scenario: options.scenario,
      isLiveUpdateEnabled: true,
      showTargetSelection: true,
      showBidAdjustment: this.supportCashbackSettings,
      showTargetOrBid: false,
      supportCashbackSettings: this.supportCashbackSettings,
      activityLogger: this.activityLogger,
    };

    this.locationSetting = new LocationCriterionSetting(_.extend({
      hideTitleLabel: true,
      deps: _.defaults(options.deps, {
        isNewUI: options.isForVNext,
        ...(this.isIMAReachRepresentationPhase1Enabled ? {
          onLocationTargetsChange: locationTargets => this.onLocationTargetsChange(locationTargets),
          isAudienceCreationWorkflow: true,
        } : {}),
      }),
    }, settingOptions, {
      showBidAdjustment: false,
    }));
    this.ageSetting = new AgeCriterionSetting(settingOptions);
    this.genderSetting = new GenderCriterionSetting(settingOptions);
    if (!this.isMSANRemoveDeviceTargetsEnabled) {
      this.deviceSetting = new DeviceCriterionSetting(settingOptions);
    }

    this.audienceSetting = new AudienceCriterionSetting(settingOptions);
    this.companySetting = new CompanyNameCriterionSetting(settingOptions);
    this.industrySetting = new IndustryCriterionSetting(settingOptions);
    this.jobFunctionSetting = new JobFunctionCriterionSetting(settingOptions);
    if (this.Enable1PPlacementTargeting) {
      this.placementSetting = new PlacementSetting(settingOptions);
    }
    this.targetSettingViews = _.compact([
      this.locationSetting,
      this.ageSetting,
      this.genderSetting,
      this.audienceSetting,
      this.companySetting,
      this.industrySetting,
      this.jobFunctionSetting,
      this.Enable1PPlacementTargeting && this.placementSetting,
    ]);

    if (this.deviceSetting) {
      this.targetSettingViews.push(this.deviceSetting);
    }

    this.reachControl = new ReachEstimateControl({
      isSticky: true,
      viewContext: this.viewContext,
      scenario: options.logScenario.value,
    });
    this.estimateRequestFactory = new EstimateRequestFactory({
      currentLevelTargetInfo: this.currentLevelTargetInfo,
    });

    this.errorsView = new AudienceCampaignErrorsView();

    if (this.isIMAReachRepresentationPhase1Enabled) {
      this.audienceSetting.setOnAudiencesTargetChangeCallback((audienceTargets) => {
        this.activityLogger.signal(`onAudienceTargetsChange: ${JSON.stringify(audienceTargets)}`);
        this.onAudienceTargetsChange(audienceTargets);
      });
    }

    this.isAudienceCampaignCollapseTargetSelectionEnabled =
      _.result(this.permissions, 'IsAudienceCampaignCollapseTargetSelectionEnabled', false);

    this.isCopyCampaignSettingsAudienceCampaignEnabled = _.result(this.permissions, 'IsCopyCampaignSettingsAudienceCampaignEnabled', false);

    if (this.isCopyCampaignSettingsAudienceCampaignEnabled) {
      const CopyAdgroupSettings = DependencyResolver.get('campaignui/feature/adgroup-create-advanced/view/copy-adgroup-settings');
      this.adGroupSettings = new CopyAdgroupSettings();
      this.adGroupSettings.on('copy.adgroup', this.adgroupCopyDriver.bind(this));
    }
  }

  /**
   * willShow() gets called from the wizard, and we take the snapshot
   * so that we can compare and save the delta
   *
   * @returns {undefined}
   */
  willShow() {
    // While it's ideal where willInit align with the contructor, it is created with the wizard.
    // The perf marker is to measure the user experience perf for the component.
    this.perfMarker.willRender();
    this.startListeningForEvents();
    this.handleEstimateUpdates();
    this.originalSnapshot = this.currentLevelTargetInfo.getSnapshot();
    this.ageSetting.saveSnapshot();
    this.genderSetting.saveSnapshot();

    if (this.deviceSetting) {
      this.deviceSetting.saveSnapshot();
    }

    if (this.supportCashbackSettings) {
      this.targetSettingViews.forEach((criterionEditor) => {
        if (criterionEditor.resetSupportCashbackSettings) {
          criterionEditor.resetSupportCashbackSettings(true, {
            level: 'AdGroup',
            value: this.viewContext.currentAdGroup.CashbackPercent,
          });
        }
      });
    }

    if (this.isContextualTargetEnabled) {
      this.contextualSelection = [];

      const contextualTargetsCriterions = this.currentLevelTargetInfo.getAllByType('ContextualTarget');
      this.contextualSelection = getSelectionFromCriterions(contextualTargetsCriterions);
      // reference copy is OK enough as the selection change comes as a new array.
      this.initialContextualSelection = this.contextualSelection;
    }
    if (this.options && this.options.perfMarker && this.renderMarker) {
      this.renderMarker.done();
    }
  }

  renderOptTgtSwtich() {
    // in campaign creation flow, we do not have campaign type and sub type until campaign created
    // so we need to get this info until adgroup creation page.

    ReactDOM.render(
      <OptTgtToggle
        ref={this.optTgtReactComponent}
        checked
        i18n={this.options.deps.i18nNewDesign}
        activityLogger={this.activityLogger}
        parent={this}
        alwaysDisabled={this.isBoostConversionBasedSegmentEnabled}
      />,
      this.$('.optimized-toggle')[0]
    );
  }

  /**
   * Get the title of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns object has a property text that is used for the title of the section.
   */
  get title() {
    return {
      text: this.i18n.getString('Audience_Campaign_Crud_Adgroup_Title'),
    };
  }

  /**
   * Get the page title of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns object has a property text that is used for the title of the section.
   */
  get pageTitle() {
    return {
      text: this.i18n.getString('Audience_Campaign_Crud_Adgroup_PageTitle'),
    };
  }

  onContextualSelectionChange(data) {
    this.contextualSelection = data || [];
  }

  /**
   * Renders the view
   *
   * @returns {View} returns the view itself
   */
  render() {
    if (this.options && this.options.perfMarker) {
      this.renderMarker = this.perfMarker.createChild('RenderCrudAdGroup');
      this.renderMarker.willInit();
    }
    // this code should be after campaign is created.
    const campaignType = _.get(this.viewContext, 'currentCampaign.CampaignType');
    const campaignSubType = _.get(this.viewContext, 'currentCampaign.CampaignSubType');
    this.isAudienceFeedBasedCampaign = (campaignType === 8 || campaignType === 'Audience') &&
      (campaignSubType === 1 || campaignSubType === 'AudienceFeedBased');
    // we are intentionally not changing the above variable name because we do not know all scenarios where it's being used
    this.isAudienceFeedBasedCampaignNew = isFeedBasedAudienceCampaign(campaignSubType);
    this.isNativeAds = (campaignType === 8 || campaignType === 'Audience') &&
      (campaignSubType === 0 || campaignSubType === 'AudienceStatic');
    this.$el.html(template(this));

    if (!this.viewContext.currentAdGroup) {
      this.nameInput = new InputControl({
        entity: this.model,
        field: 'Name',
        ariaLabelledby: 'adgroupNameLabel',
      });

      this.nameInput.on('input-changed', () => {
        this.activityLogger.signal('adgroup name change', 'crud audience adgroup');
      });

      if (this.isAudienceCampaignCollapseTargetSelectionEnabled) {
        this.nameInput.setState({ value: generateNameSuggestionSuffix('Adgroup') });
      }

      this.nameInputRemainingCharsHint = new RemainingCharsHint({
        i18n: this.i18n,
        model: this.model,
        field: 'Name',
      });

      this.$('.name-input-control-container').prepend(this.nameInput.render().$el);
      this.$('.remaining-chars-hint-container').append(this.nameInputRemainingCharsHint.render().$el);
    } else {
      this.$('.adgroup-name-input-container').hide();
    }

    if (this.isMSANBoostEnabled) {
      ReactDOM.render(
        <BoostPlacementTarget ref={this.boostPlacementReactComponent} i18n={this.options.deps.i18nNewDesign} />,
        this.$('.boost-placement-target')[0]
      );
    }

    if (this.isContextualTargetEnabled) {
      ReactDOM.render(
        <ContextualTargetWithStyle
          i18n={this.options.deps.i18nNewDesign}
          contextualSelection={this.contextualSelection}
          onContextualSelectionChange={selection => this.onContextualSelectionChange(selection)}
        />,
        this.$('.contextual-target-container')[0]
      );
    }

    if (!this.isSimplifyCampaignCreation) {
      this.$('.opttgt-setting').append('<div class="optimized-container"></div>');
      this.$('.optimized-container').html(collapseTemplate({ isFeedBasedAudienceCampaign: this.isAudienceFeedBasedCampaign, i18n: this.i18n }));
      this.$('.optTgt-setting-container').append(this.audienceTargetSelector.render().$el);

      this.$('.optTgt-setting-container .crud-audience-target-selector .crud-audience-target-selector__description').text(this.i18n.getString('OptimizedTargeting_Expand_Description'));
      if (!this.isAudienceFeedBasedCampaign) {
        this.renderOptTgtSwtich();
        this.$('.optimized-targeting-description').html(`${this.i18n.getString('Audience_Campaign_Predictive_Targeting_Description')}<a class="HelpTopic" query="help:app54818">${this.i18n.getString('Audiences_GoToAdgroupTab_LearnMore')}</a>`);
      }
    } else {
      this.$('.audience-target-selector-container').append(this.audienceTargetSelector.render().$el);

      if (this.isSimplifyCampaignCreation && !this.isAudienceFeedBasedCampaign) {
        this.renderOptTgtSwtich();
      }
    }

    if (!this.isSimplifyCampaignCreation && !this.isAudienceFeedBasedCampaign) {
      this.$('.audience-reach-estimate-container').append(this.reachControl.render().$el);
    }

    this.$('.errors-container').append(this.errorsView.render().$el);

    if (this.isAudienceCampaignCollapseTargetSelectionEnabled) {
      this.audienceTargetSelector.$('.location-setting-container').append(this.locationSetting.render().$el);
      this.audienceTargetSelector.$('.age-setting-container').append(this.ageSetting.render().$el);
      this.audienceTargetSelector.$('.gender-setting-container').append(this.genderSetting.render().$el);
      this.audienceTargetSelector.$('.audience-setting-container').append(this.audienceSetting.render().$el);
      this.audienceTargetSelector.$('.companyName-setting-container').append(this.companySetting.render().$el);
      this.audienceTargetSelector.$('.industry-setting-container').append(this.industrySetting.render().$el);
      this.audienceTargetSelector.$('.jobFunction-setting-container').append(this.jobFunctionSetting.render().$el);
      if (this.Enable1PPlacementTargeting && this.isNativeAds) {
        this.audienceTargetSelector.$('.placement-setting-container').append(this.placementSetting.render().$el);
      }
      // this.audienceTargetSelector.$('.control-label').removeClass();
      this.audienceTargetSelector.$('.control-label').removeClass('show');
      // end of my changes
    } else {
      this.$('.location-setting-container').append(this.locationSetting.render().$el).hide();
      this.$('.age-setting-container').append(this.ageSetting.render().$el).hide();
      this.$('.gender-setting-container').append(this.genderSetting.render().$el).hide();

      // All the device targets are all selected
      // this.$('.device-setting-container').append(this.deviceSetting.render().$el).hide();
      this.$('.audience-setting-container').append(this.audienceSetting.render().$el).hide();
      this.$('.companyName-setting-container').append(this.companySetting.render().$el).hide();
      this.$('.industry-setting-container').append(this.industrySetting.render().$el).hide();
      this.$('.jobFunction-setting-container').append(this.jobFunctionSetting.render().$el).hide();
      if (this.Enable1PPlacementTargeting && this.isNativeAds) {
        this.$('.placement-setting-container').append(this.placementSetting.render().$el).hide();
      }
    }
    this.perfMarker.done();
    if (this.isCopyCampaignSettingsAudienceCampaignEnabled) {
      this.$('.copy-adgroup-settings-container').append(this.adGroupSettings.render().$el);
    }
    return this;
  }

  getAdGroupData() {
    let params = null;
    let isUpdateScenario = false;
    if (this.viewContext.currentAdGroup) {
      params = this.viewContext.currentAdGroup;
      isUpdateScenario = true;
    } else {
      params = this.model.toJSON();
      params.Campaign = { Id: this.viewContext.currentCampaign.Id };
      params.AdScheduleUseSearcherTimeZone = false;
      if (this.viewContext.currentCampaign.CampaignSubType === 'AudienceVideoAds') {
        params.PricingModel = 'CPV';
      } else if (this.isAdImpressions) {
        params.PricingModel = 'CPM';
      }
    }

    if (this.isMSANBoostEnabled) {
      params = {
        ...params,
        ...this.boostPlacementReactComponent.current.getData(),
      };
    }

    if (this.isAudienceFeedBasedCampaign) {
      params.UseOptimizedTargeting = true;
    } else {
      const useOptimizedTargeting = this.optTgtReactComponent.current.getData();
      params.UseOptimizedTargeting = useOptimizedTargeting;
      // for adgroup creation scenario, we always set it as true, MT is going to throw error if value is false.
      if (this.isSimplifyCampaignCreation || !isUpdateScenario) {
        params.UseOptimizedTargeting = true;
      }
    }

    if (this.Enable1PPlacementTargeting && !this.isAudienceFeedBasedCampaignNew) {
      params.BoostAdunitIds = this.placementSetting.getSnapshot();
    }

    return params;
  }

  /**
   * Get the data that is used for the parameter for saving.
   *
   * @returns {Object} - object representing the data for the adgroup to be saved.
   */
  getDataAsync() {
    const params = this.getAdGroupData();

    // Let the checkbox save changes to the currentLevelTargetInfo
    const updateTasks = [
      this.ageSetting.updateCriterionDataAsync(),
      this.genderSetting.updateCriterionDataAsync(),
    ];

    if (this.deviceSetting) {
      updateTasks.push(this.deviceSetting.updateCriterionDataAsync());
    }

    // Get the audience criterions from the
    return Promise.all(updateTasks).then(() => {
      // Location and Device are not supported for TargetSetting property
      let targetTypes = this.currentLevelTargetInfo.getAllNoneEmptyTypeKeys();
      targetTypes = _.reject(
        targetTypes,
        targetType => targetType === 'Location' ||
          targetType === 'Radius' ||
          targetType === 'NegativeLocation' ||
          targetType === 'Device' ||
          targetType === 'ContextualTarget'
      );

      params.TargetSetting = targetTypes.join(';');

      const currentSnapshot = this.currentLevelTargetInfo.getSnapshot();

      let targets =
        criterionHelper.compareAndGetCriterionsDelta(this.originalSnapshot, currentSnapshot);

      // Do not need to save NegativeAge, Gender and Device
      targets = _.reject(targets, target => (
        target.CriterionType === 'NegativeAge' ||
        target.CriterionType === 'NegativeGender' ||
        target.CriterionType === 'NegativeDevice'));

      if (!this.currentLevelTargetInfo.listenToLocationCriterion) {
        // use the targets that are returned from the location target setting control
        targets = _.reject(targets, target => (
          target.CriterionType === 'Location' ||
          target.CriterionType === 'Radius' ||
          target.CriterionType === 'NegativeLocation'));

        const {
          intent = [],
          locations = [],
          deletedLocations = [],
        } = this.locationSetting.getData();
        const locationCriterions = intent.concat(locations).concat(deletedLocations);

        targets = targets.concat(locationCriterions);
      }

      if (this.isContextualTargetEnabled) {
        const contextualTargets = getContextualTargetInfo(
          this.initialContextualSelection,
          this.contextualSelection
        );

        targets = targets.concat(contextualTargets);
      }

      const processedTargets = [];
      _.each(targets, (target) => {
        const campaignName = this.viewContext.currentCampaign.Name;
        const adgroupName = this.model.get('Name');
        let processedTarget = cloneDeep(target);

        // remove virtual negative IDs
        if (parseInt(processedTarget.Id, 10) < 0) {
          processedTarget.Id = undefined; // eslint-disable-line
        }

        // if no adgroup exist, remove campaign and adgroup property
        if (this.viewContext.currentAdGroup) {
          processedTarget.AdGroup = { // eslint-disable-line
            Id: this.viewContext.currentAdGroup.Id,
          };
        } else {
          processedTarget = _.omit(processedTarget, 'Campaign'); // eslint-disable-line
          processedTarget = _.omit(processedTarget, 'AdGroup'); // eslint-disable-line
        }

        processedTarget = _.extendOwn(processedTarget, {
          CampaignName: campaignName,
          AdGroupName: adgroupName,
        });

        processedTargets.push(processedTarget);
      });

      const adGroupCriterions = _.reject(processedTargets, target => (
        target.Criterion.Type === 'Audience'));
      const adGroupAudienceCriterions = _.filter(processedTargets, target => (
        target.Criterion.Type === 'Audience'));

      params.AdGroupCriterions = adGroupCriterions;
      params.AdGroupAudienceCriterions = adGroupAudienceCriterions;

      return params;
    });
  }

  /**
   * It attempts to save and return true if the save is done succefully.
   *
   * @returns {boolean} - returns true if it is ready to go to the next step.
   */
  wizardShouldGoNext() {
    this.activityLogger.trace('wizard should go next is called');
    return this.validateAndSave().then((isSuccess) => {
      if (isSuccess) {
        this.stopListeningForEvents();
      }

      this.activityLogger.trace(`validate and save result: ${isSuccess}`);
      this.activityLogger.signal(`clicked next: ${isSuccess}`, 'crud audience adgroup');
      return isSuccess;
    });
  }

  /**
   * It prepares to go back to the previous step.
   *
   * @returns {boolean} - returns true if it is ready to go to the back.
   */
  wizardShouldGoBack() {
    return this.validateAndSave().then((isSuccess) => {
      if (isSuccess) {
        this.stopListeningForEvents();
      }

      this.activityLogger.signal(`clicked back: ${isSuccess}`, 'crud audience adgroup');

      return isSuccess;
    });
  }

  /**
   * Start listening for the events
   * @private
   *
   * @returns {undefined}
   */
  startListeningForEvents() {
    this.listenTo(
      this.audienceTargetSelector,
      'selectionChanged',
      this.onSelectionChanged
    );
    this.listenTo(
      this.currentLevelTargetInfo,
      'AdGroupCriterionChanged',
      this.handleEstimateUpdates
    );

    this.listenTo(
      this.currentLevelTargetInfo,
      'AdGroupCriterionChanged',
      this.disableOptToggle
    );

    if (this.isAudienceCampaignCollapseTargetSelectionEnabled) {
      this.listenTo(
        this.currentLevelTargetInfo,
        'AdGroupCriterionChanged',
        this.onReloadTargetSummary
      );
    }
  }

  disableOptToggle() {
    if (!this.isAudienceFeedBasedCampaign && this.optTgtReactComponent && this.optTgtReactComponent.current) {
      this.optTgtReactComponent.current.disableByValidation.call(this.optTgtReactComponent.current);
    }
  }

  /**
   * Stop listening for the events
   * @private
   *
   * @returns {undefined}
   */
  stopListeningForEvents() {
    this.stopListening(
      this.audienceTargetSelector,
      'selectionChanged',
      this.onSelectionChanged
    );
    this.stopListening(
      this.currentLevelTargetInfo,
      'AdGroupCriterionChanged',
      this.handleEstimateUpdates
    );

    this.stopListening(
      this.currentLevelTargetInfo,
      'AdGroupCriterionChanged',
      this.disableOptToggle
    );

    if (this.isAudienceCampaignCollapseTargetSelectionEnabled) {
      this.stopListening(
        this.currentLevelTargetInfo,
        'AdGroupCriterionChanged',
        this.onReloadTargetSummary
      );
    }
  }

  /**
   * Saves the AdGroup and exits.
   * This is only used when 'Save & Exit' button is shown on this step.
   *
   * @returns {undefined} - returns undefined
   */
  saveAndExit() {
    this.validateAndSave().then((isSuccess) => {
      this.activityLogger.signal(`save and exit: ${isSuccess}`, 'crud audience adgroup');
      if (isSuccess) {
        this.trigger('saveAndExit');
        if (this.isOpenCreatePanel && this.isLastStep) {
          const mes = { CampaignId: this.viewContext.currentCampaign.Id, DeliveryStatusType: 'NoEligibleAdGroups' };

          this.applyActivity.trace(JSON.stringify(mes));
        }
      }
    });
  }

  /**
   * Validates controls on page
   *
   * @returns {boolean} - returns true if succesful
   */
  validate() {
    if (this.nameInput && !this.nameInput.getState().isValid) {
      this.nameInput.setState({ showError: true });
      this.nameInput.$el[0].scrollIntoView(false);

      this.activityLogger.trace(`validation failed due to name input: ${this.nameInput.getState()}`);

      return false;
    }

    if (!this.isAudienceFeedBasedCampaign) {
      const isTargetsValid = this.optTgtReactComponent.current && this.optTgtReactComponent.current.validate();
      // when target to all set to On
      if (!isTargetsValid) {
        this.optTgtReactComponent.current.setState({
          checked: true,
        });
      }
    }

    if (!this.locationSetting.validate() ||
      !this.ageSetting.validate() ||
      !this.genderSetting.validate() ||
      !(!this.deviceSetting || this.deviceSetting.validate()) ||
      !this.audienceSetting.validate() ||
      !this.companySetting.validate() ||
      !this.industrySetting.validate() ||
      !this.jobFunctionSetting.validate()) {
      this.activityLogger.trace('validation failed due to one of the target settings');

      return false;
    }

    return true;
  }

  /**
   * It validates the reach and privacy of selected targets and then
   * attempts to save and return true if the save is done succefully.
   *
   * @returns {boolean} - returns true if save was succesful
   */
  validateAndSave() {
    if (!this.validate()) {
      return Promise.resolve(false);
    }

    return this.reachControl.validate().then((isValid) => {
      this.activityLogger.trace(`reach control validation result: ${isValid}`);
      if (isValid) {
        return this.checkIMAReachAndSave();
      }
      const message = this.reachControl.errorMessage;

      this.errorsView.setErrors([{ message }]);

      return false;
    }).catch(() => {
      // TODO: log here
    });
  }

  checkIMAReachAndSave() {
    const currentSnapshot = this.currentLevelTargetInfo.getSnapshot();
    const audienceIds = [];
    const audienceTargets = _.chain(currentSnapshot)
      .filter(item => item.CriterionType === 'InMarket')
      .reduce((memo, item) => {
        const audienceId = item.Criterion.AudienceId;

        if (_.indexOf(audienceIds, audienceId) === -1) {
          audienceIds.push(audienceId);
          memo.push({
            Level: 'AdGroup',
            AudienceCriterion: {
              AudienceId: item.Criterion.AudienceId,
              AudienceName: item.Criterion.AudienceName,
            },
          });
        }

        return memo;
      }, [])
      .value();
    let locationCriterions;

    if (!this.currentLevelTargetInfo.listenToLocationCriterion) {
      // use the targets that are returned from the location target setting control
      const {
        intent = [],
        locations = [],
      } = this.locationSetting.getAllLocations();
      locationCriterions = intent.concat(locations);
      locationCriterions = _.filter(locationCriterions, c => c.Status !== 'Deleted');

      const adGroupCriterionsWithoutIntent = _.filter(locationCriterions, c => c.CriterionType !== 'LocationIntentCriterion');
      const campaignCriterions = this.locationSetting.getCampaignLocationCriterions();

      // inherit locations
      if (adGroupCriterionsWithoutIntent.length === 0 &&
        campaignCriterions &&
        campaignCriterions.length > 0) {
        const overrideInfo = getLocationsInfoFromCampaignCriterions(campaignCriterions);
        locationCriterions = overrideInfo.locations.concat(overrideInfo.intent);
      }
    } else {
      locationCriterions = _.filter(currentSnapshot, target => (
        target.CriterionType === 'Location' ||
        target.CriterionType === 'Radius' ||
        target.CriterionType === 'NegativeLocation'));

      locationCriterions = _.filter(locationCriterions, c => c.Status !== 'Deleted');

      const campaignCriterions = this.currentLevelTargetInfo.getCampaignLocationCriterions();

      // inherit locations
      if (locationCriterions.length === 0 &&
        campaignCriterions &&
        campaignCriterions.length > 0) {
        locationCriterions = _.filter(campaignCriterions, c => c.Status !== 'Deleted');
      }
    }

    if (this.isIMAReachRepresentationPhase1Enabled && this.isForVNext &&
      locationCriterions.length > 0 && audienceTargets.length > 0) {
      return new Promise((resolve) => {
        const onYes = () => {
          this.saveAsync().then((isSuccess) => {
            resolve(isSuccess);
          });
        };
        const onCancel = () => resolve(false);
        const locationsInfo = getLocationsInfo(locationCriterions);
        showIMAReachModal({
          locationsInfo,
          onYes,
          onCancel,
          deliveryChannel: 'AIM',
          // this adgroupId is needed to indicate it's ad group level
          // we don't care about it's value
          adGroupId: this.viewContext.currentAdGroup && this.viewContext.currentAdGroup.Id ?
            Number(this.viewContext.currentAdGroup.Id) : 1,
          audienceAssociations: { value: audienceTargets },
          i18n: this.i18n,
          aid: this.accountInfo.Id,
          cid: this.accountInfo.AdvertiserCustomerID,
        }).then((isIMAReachModalShow) => {
          if (!isIMAReachModalShow) {
            this.saveAsync().then((isSuccess) => {
              resolve(isSuccess);
            });
          }
        });
      });
    }
    return this.saveAsync();
  }

  /**
   * Save the adgroup with the given setting.
   *
   * @returns {Promise} - returns promise of returning boolean value indicating
   * if the save process is succesful or not.
   */
  saveAsync() {
    const jsdata = DependencyResolver.get('component/js-data-campaign/index');

    this.errorsView.removeErrors();

    let createTask;

    // If saved already
    if (this.viewContext.currentAdGroup && this.viewContext.currentAdGroup.Id) {
      createTask = Promise.resolve(true);
    } else {
      const adGroupParams = this.getAdGroupData();

      this.activityLogger.trace(`creating the ad group with the params: ${adGroupParams}`);

      createTask = jsdata.AdGroup.create(
        adGroupParams,
        { adapter: 'bulk', cacheResponse: false }
      ).then((data) => {
        if (_.isFinite(data.Id)) {
          this.viewContext.currentAdGroup = data;

          return true;
        }

        return false;
      });
    }

    return createTask
      .then(() =>
        this.getDataAsync())
      .then(params => jsdata.AdGroup.update(
        params.Id,
        params,
        { adapter: 'bulk', cacheResponse: false }
      ))
      .then((data) => {
        if (_.isFinite(data.Id)) {
          this.viewContext.currentAdGroup = data;

          // This will be reused from step4.
          this.viewContext.audienceSetting = this.audienceSetting;

          const savedAdGroupCriterions = data.AdGroupCriterions ? data.AdGroupCriterions : [];
          const savedAudienceAdGroupCriterions =
            data.AdGroupAudienceCriterions ? data.AdGroupAudienceCriterions : [];

          return this.postProcessAfterSaveAsync(
            savedAdGroupCriterions,
            savedAudienceAdGroupCriterions
          );
        }

        return Promise.resolve(false);
      }).catch((response) => {
        this.handleErrors(response);

        return Promise.resolve(false);
      })
      .finally(withUiBlock(this.wizardElement, this.i18n.getString('Loading')));
  }

  handleErrors(response) {
    const createAudienceTargetsActivity = this.logScenario.createAudienceTargets.create();

    if (response && response.entity) {
      const hasErrors = response && !_.isUndefined(response.errors);

      if (hasErrors) {
        if (response.errors.length) {
          const bulkApiErrors = this.i18nErrors.getBulkApiErrors(response.errors);
          const noMappedErrors = _.every(bulkApiErrors, error => error.code === error.message);

          if (_.isEmpty(bulkApiErrors) || noMappedErrors) {
            this.errorsView.setErrors([{ message: this.i18n.getString('GenericErrorMessage_Short') }]);

            createAudienceTargetsActivity.error(`${getBulkErrorsLogMessage(response.errors)}, Entity:${JSON.stringify(response.entity)}`);
          } else {
            this.errorsView.setErrors(bulkApiErrors);

            createAudienceTargetsActivity.trace(getBulkErrorsLogMessage(response.errors));
            _.each(response.errors, (error) => {
              this.activityLogger.signal(error.Error, 'audience campaign creation api error');
            });
          }
        }
      } else {
        // Unexpected case. Log Error.
        createAudienceTargetsActivity.error(JSON.stringify(response));
      }
    } else {
      // Ajax error
      this.errorsView.setErrors(this.i18nErrors.getODataErrors(response.errors));
    }
  }

  /**
   * Post processing after the save.
   * This ensures that updated criterions from the server are saved on the cache
   * @private
   *
   * @param {Array} savedAdGroupCriterions - array of adgroup criterions not including audience
   * @param {Array} savedAudienceAdGroupCriterions - array of audience type adgroup criterions
   * @returns {Promise} - promise of cache being settled.
   */
  postProcessAfterSaveAsync(savedAdGroupCriterions = [], savedAudienceAdGroupCriterions = []) {
    const targetInfo = this.currentLevelTargetInfo;
    if (savedAdGroupCriterions.length > 0 || savedAudienceAdGroupCriterions.length > 0) {
      return targetInfo.destroyAllAsync().then(() => {
        const updatedCriterions =
          criterionHelper.applyCriterionsDelta(
            this.originalSnapshot,
            _.union(savedAdGroupCriterions, savedAudienceAdGroupCriterions)
          );

        return targetInfo.createMultipleAsync(updatedCriterions).then(() => true);
      });
    }

    // No criterion saved, just move on.
    return Promise.resolve(true);
  }

  /**
   * Get the actions of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns array that has information about the actions.
   */
  get actions() {
    const actions = [
      {
        name: 'back',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Back_Label'),
      },
      {
        name: 'done',
        htmlAttributes: {
          class: 'btn-primary',
        },
        enabled: true,
        method: 'saveAndExit',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Save_Exit'),
      },
    ];

    if (this.scenario === 'adgroup-creation' && this.audienceCampaignType === 'shopping') {
      // use the actions as is
    } else if (this.scenario === 'campaign-creation' || this.scenario === 'adgroup-creation') {
      // need the next button at the top.
      actions.unshift({
        name: 'next',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Save_Next'),
      });
    } else {
      return super.actions;
    }

    return actions;
  }

  // private function
  // event handler for handling the section changed.
  onSelectionChanged(containerId) {
    this.$(this.getContainerNameFromId(containerId)).show();
  }

  // private function
  // returning container name by the given id.
  getContainerNameFromId(id) {
    return `.${id}-setting-container`;
  }

  // private function
  // event handler for handling the updating of reach estimates.
  handleEstimateUpdates(parent = null) {
    const ts = parent == null ? this : parent;
    const { currentCampaign } = ts.viewContext;
    // This can be undefined if it's first displayed but has value when come from next step
    const biddingSchemeType = currentCampaign &&
      currentCampaign.BiddingScheme &&
      currentCampaign.BiddingScheme.Type;
    const bidinfo = BidStrategy[biddingSchemeType];
    const subType = currentCampaign &&
      currentCampaign.CampaignSubType;
    const campaignSetting = _.first(_.get(currentCampaign, 'CampaignSettings')) || {};
    const feedInfo = getFeedInfo(campaignSetting);
    const useOptimizedTargeting = ts.optTgtReactComponent.current &&
      ts.optTgtReactComponent.current.getData();
    const targets = ts.estimateRequestFactory.create(bidinfo, subType, feedInfo, useOptimizedTargeting);
    const hasAudienceRemarketingList = ts.currentLevelTargetInfo.hasAudienceRemarketingList();

    ts.reachControl.updateEstimate(targets, hasAudienceRemarketingList);
  }

  remove() {
    this.audienceTargetSelector.remove();
    this.locationSetting.remove();
    this.ageSetting.remove();
    this.genderSetting.remove();

    if (this.deviceSetting) {
      this.deviceSetting.remove();
    }

    this.audienceSetting.remove();
    this.companySetting.remove();
    this.industrySetting.remove();
    this.jobFunctionSetting.remove();
    this.reachControl.remove();
    this.errorsView.remove();
    if (this.nameInput) {
      this.nameInput.remove();
    }
    if (this.nameInputRemainingCharsHint) {
      this.nameInputRemainingCharsHint.remove();
    }
    super.remove();
  }

  onLocationTargetsChange(locationTargets) {
    // subscribed location targets changes
    const {
      locationIntentCriterion,
      targetedLocationCriterions,
      excludedLocationCriterions,
    } = getLocationsInfo(locationTargets);

    // refresh audience association components
    return this.audienceSetting.reloadAudienceData({
      targetedLocationCriterions,
      excludedLocationCriterions,
      locationIntentCriterion,
      isLocationRestricted: false,
    });
  }

  onAudienceTargetsChange(audienceTargets) {
    // subscrib audienceTargets changes
    // refresh location components
    this.locationSetting.reloadLocationView(audienceTargets);
  }

  deselectAll() {
    // eslint-disable-next-line max-len
    if (this.locationSetting.targetSelectionView && this.locationSetting.targetSelectionView.locationTargetsView) {
      this.locationSetting.targetSelectionView.locationTargetsView.settoSpecificLocation();
    }

    const targetTypes = [
      'Radius',
      'Location',
      'NegativeLocation',
      'Audience',
      'CompanyName',
      'NegativeCompanyName',
      'JobFunction',
      'NegativeJobFunction',
      'Industry',
      'NegativeIndustry',
    ];
    _.each(targetTypes, (targetType) => {
      this.currentLevelTargetInfo.destroyTargetTypeAsync(targetType);
    });
  }

  // isLocation: Locations must be handled seperately.
  // locType: True = location, False = Radius
  // isIncluded: This is specifically for location use.
  criterionFormat(formatted, criterions, isLocation = false, locType = false, isIncluded = false) {
    _.each(criterions, (loadedCriterionData) => {
      const formattedCriterion = criterionHelper.reformatFetchedCriterion(
        loadedCriterionData,
        loadedCriterionData.CampaignId,
        loadedCriterionData.AdGroupId
      );

      // Reassign the name if it was not properly assigned.
      if (!formattedCriterion.Criterion.Name) {
        formattedCriterion.Criterion.Name = loadedCriterionData.Criterion.AgeRange ||
          loadedCriterionData.Criterion.GenderType || loadedCriterionData.Criterion.GenderType ||
          loadedCriterionData.Criterion.IntentOption || loadedCriterionData.Criterion.CompanyName ||
          loadedCriterionData.Criterion.JobFunction || loadedCriterionData.Criterion.Industry;
      }

      if (isLocation && this.locationSetting.targetSelectionView.locationTargetsView) {
        // Locations need to be handled with a specific format. Use helper function.
        // eslint-disable-next-line max-len
        this.locationSetting.targetSelectionView.locationTargetsView.locationManageView.searchView.convertFromCriterionHelperFormat(locType, formattedCriterion, isIncluded);
      } else {
        // All other criterions are added to this list for later use.
        formatted.push({
          Criterion: formattedCriterion.Criterion,
          CriterionType: formattedCriterion.CriterionType,
        });
      }
    });
  }

  copyAdgroupCriterion(source) {
    const adgroup = source[0];
    this.activityLogger.signal(`Copy setting from setting: ${adgroup.Name} ${adgroup.Id}`, 'Audience Copy Ad Group Setting');

    const criterions = source[1];
    // Negative Criterions do not have the CriterionBid field. Use this to check.
    const ageKeys = criterions.filter(i => i.Criterion.Type === 'Age' && i.CriterionBid).map(i => i.Criterion.AgeRange);
    const genderKeys = criterions.filter(i => i.Criterion.Type === 'Gender' && i.CriterionBid).map(i => i.Criterion.GenderType);
    const locations = criterions.filter(i => i.Criterion.Type === 'Location' && i.CriterionBid);
    const radiusList = criterions.filter(i => i.Criterion.Type === 'Radius' && i.CriterionBid);
    // All location permutations must be handled seperately.
    const excludedLocations = criterions.filter(i => i.Criterion.Type === 'Location' && !i.CriterionBid);
    const otherCriterions = criterions.filter(i => i.Criterion.Type === 'CompanyName'
      || i.Criterion.Type === 'JobFunction' || i.Criterion.Type === 'Industry');

    if (!_.isEmpty(ageKeys)) {
      this.ageSetting.targetSelectionView.setKeys(ageKeys);
    }

    if (!_.isEmpty(genderKeys)) {
      this.genderSetting.targetSelectionView.setKeys(genderKeys);
    }

    const formattedCriterions = [];

    // All other non-checkbox Criterion.
    this.criterionFormat(formattedCriterions, otherCriterions);
    // This adds all non-location criterions to the criterion list.
    this.currentLevelTargetInfo.createMultipleAsync(formattedCriterions);

    _.delay(() => {
      // Audience Criterions
      if (source[2] && source[2].value) {
        // const audienceCriterions = [];
        this.currentLevelTargetInfo.audienceAssociationView.expandAssociation();
        this.currentLevelTargetInfo.audienceAssociationView.expandExclusion();
        const { configSettings } = this.currentLevelTargetInfo.audienceAssociationView.doubleListBoxViewModel;
        _.each(source[2].value, (criterion) => {
          this.audienceSetting.convertAudienceMTCriterion(criterion.AdGroupCriterion, configSettings);
        });
      }
    }, 3000);

    // Included Locations
    this.criterionFormat(formattedCriterions, locations, true, true, true);
    // Excluded Locations
    this.criterionFormat(formattedCriterions, excludedLocations, true, true, false);
    // Radius. We do not support Excluded Radius yet.
    this.criterionFormat(formattedCriterions, radiusList, true, false, false);
  }

  adgroupCopyDriver(source) {
    // First clear out the existing settings.
    this.deselectAll();

    // Fill in the criterion
    this.copyAdgroupCriterion(source);

    _.delay(() => {
      // UseOptimizedTargeting
      if (source[0] && source[0].UseOptimizedTargeting != null) {
        this.optTgtReactComponent.current.onChange(null, source[0].UseOptimizedTargeting);
      }
    }, 3000);
  }

  /* updates the summary under the auidence target selection
     when collapse is closed.
  */
  onReloadTargetSummary() {
    const locationTSummary = this.locationSetting.getTargetedSummary();
    const locationESummary = this.locationSetting.getExcludedSummary();
    const ageSummary = this.ageSetting.getSummary();
    const genderSummary = this.genderSetting.getSummary();
    const audienceTSummary = this.audienceSetting.getTargetedSummary();
    const audienceESummary = this.audienceSetting.getExcludedSummary();
    const companyTSummary = this.companySetting.getTargetedSummary();
    const companyESummary = this.companySetting.getExcludedSummary();
    const industryTSummary = this.industrySetting.getTargetedSummary();
    const industryESummary = this.industrySetting.getExcludedSummary();
    const jobTSummary = this.jobFunctionSetting.getTargetedSummary();
    const jobESummary = this.jobFunctionSetting.getExcludedSummary();

    this.audienceTargetSelector.dataModel.locationSummaryText(`${locationTSummary}\n${locationESummary}`);
    this.audienceTargetSelector.dataModel.ageSummaryText(ageSummary);
    this.audienceTargetSelector.dataModel.genderSummaryText(genderSummary);
    this.audienceTargetSelector.dataModel.audienceSummaryText(`${audienceTSummary}\n${audienceESummary}`);
    this.audienceTargetSelector.dataModel.companyNameSummaryText(`${companyTSummary}\n${companyESummary}`);
    this.audienceTargetSelector.dataModel.industrySummaryText(`${industryTSummary}\n${industryESummary}`);
    this.audienceTargetSelector.dataModel.jobFunctionSummaryText(`${jobTSummary}\n${jobESummary}`);
  }
}
